import React, { Component } from "react";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";

export const COLOR_NEW = "#c7d2fe";
export const COLOR_CLOSED = "#4f46e5";
const LABEL_COLOR_NEW = "#9ca3af";
const LABEL_COLOR_CLOSED = "#4f46e5";

/*
Grouped bar chart: new vs closed tickets per bucket.

Follows the imperative chart.js lifecycle used elsewhere in this app
(pages/Dashboard/PieChartAllocate.js) rather than introducing react-chartjs-2,
which is in package.json but unused.

Note: the canvas deliberately carries no `id` - two existing chart components
hardcode id="myChart", and a third duplicate would collide.
*/
export default class IntakeChart extends Component {
  chartRef = React.createRef();
  chartInstance = null;

  componentDidMount() {
    this.buildChart();
  }

  componentDidUpdate(prevProps) {
    // Reference comparison: the parent must pass the array through unchanged,
    // never `buckets={data?.chart?.buckets || []}` (a fresh array each render).
    if (prevProps.buckets !== this.props.buckets) {
      this.buildChart();
    }
  }

  componentWillUnmount() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  }

  buildChart() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
    // The skeleton swap can unmount the canvas between render and this call.
    if (!this.chartRef.current) return;

    const buckets = this.props.buckets || [];
    const ctx = this.chartRef.current.getContext("2d");
    const self = this;

    // Click-to-filter: dataset 0 is "new", 1 is "closed". `onBarClick` is read
    // from props at click time, since the chart is only rebuilt when `buckets`
    // changes. A bar is only clickable when its bucket carries that filter.
    const barAt = function (elements) {
      if (!self.props.onBarClick || !elements || !elements.length) return null;
      const bucket = buckets[elements[0].index];
      const barKind = elements[0].datasetIndex === 0 ? "new" : "closed";
      if (!bucket || !bucket.filters || !bucket.filters[barKind]) return null;
      return { bucket, barKind };
    };

    this.chartInstance = new Chart(ctx, {
      type: "bar",
      plugins: [ChartDataLabels],
      data: {
        labels: buckets.map(function (b) {
          return b.label;
        }),
        datasets: [
          {
            label: "New",
            data: buckets.map(function (b) {
              return b.new;
            }),
            backgroundColor: COLOR_NEW,
            borderRadius: 6,
            borderSkipped: false,
            datalabels: { color: LABEL_COLOR_NEW },
          },
          {
            label: "Closed",
            data: buckets.map(function (b) {
              return b.closed;
            }),
            backgroundColor: COLOR_CLOSED,
            borderRadius: 6,
            borderSkipped: false,
            datalabels: { color: LABEL_COLOR_CLOSED },
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        categoryPercentage: 0.7,
        barPercentage: 0.85,
        // Headroom for the datalabels drawn above each bar.
        layout: { padding: { top: 24 } },
        onClick: function (event, elements) {
          const hit = barAt(elements);
          if (hit) self.props.onBarClick(hit.bucket, hit.barKind);
        },
        onHover: function (event, elements) {
          const target = event.native && event.native.target;
          if (target) target.style.cursor = barAt(elements) ? "pointer" : "default";
        },
        plugins: {
          // Custom HTML legend above the canvas - chart.js's built-in one
          // can't be styled to match the design.
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: function (items) {
                if (!items.length) return "";
                const bucket = buckets[items[0].dataIndex];
                if (!bucket) return items[0].label;
                if (bucket.start && bucket.end) {
                  return bucket.label + " (" + bucket.start + " – " + bucket.end + ")";
                }
                return bucket.label;
              },
            },
          },
          datalabels: {
            anchor: "end",
            align: "top",
            font: { family: "Onest, Roboto, sans-serif", size: 12, weight: "600" },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: {
              color: "#6b7280",
              font: { family: "Onest, Roboto, sans-serif", size: 11 },
            },
          },
          y: {
            beginAtZero: true,
            display: false,
          },
        },
      },
    });
  }

  render() {
    return (
      <div className="tn-chart">
        {/* maintainAspectRatio:false needs a fixed-height parent, or the
            canvas grows without bound on every resize. */}
        <div className="tn-chart-canvas">
          <canvas ref={this.chartRef} />
        </div>
      </div>
    );
  }
}
